import {
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import { FC, MouseEvent, ReactNode, useState } from 'react';

const StyledList = styled(List)(({ theme }) => ({
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100],
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(2, 5, 2, 3),
    position: 'relative',
}));

const StyledListItemIcon = styled(ListItemIcon)({
    minWidth: 0,
});

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.subtitle1.fontSize,
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
    '& [class*="MuiTabs-indicator"]': {
        display: 'none',
    },
    '& [class*="Mui-selected"]': {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.grey[100],
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    minWidth: 0,
    padding: theme.spacing(2, 5),
    marginBottom: '-4px',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.subtitle1.fontSize,
}));

interface TabsBarProps {
    tabs: ReactNode[];
    active: number;
    onChange: (tabIndex: number) => void;
}

export const TabsBar: FC<TabsBarProps> = ({ tabs, active, onChange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleChange = (_, idx: number) => {
        onChange(idx);
        handleClose();
    };

    const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return isMobile ? (
        <>
            <StyledList disablePadding>
                <StyledListItem button onClick={handleOpen}>
                    <StyledListItemIcon>
                        <ArrowDropDown color="primary" />
                    </StyledListItemIcon>
                    <StyledListItemText disableTypography>{tabs[active]}</StyledListItemText>
                </StyledListItem>
            </StyledList>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}>
                {tabs.map((tab, i) => (
                    <MenuItem key={i} selected={i === active} onClick={e => handleChange(e, i)}>
                        {tab}
                    </MenuItem>
                ))}
            </Menu>
        </>
    ) : (
        <StyledTabs value={active} onChange={handleChange} variant="scrollable">
            {tabs.map((tab, i) => (
                <StyledTab key={i} value={i} label={tab} />
            ))}
        </StyledTabs>
    );
};
